import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthentication } from 'src/features/authentication/context';
import { useGetBillingStatusQuery } from 'src/hooks/reactQueryApiHooks';
import {
  hasViewedBillingInformation,
  markBillingInformationViewed,
} from 'src/features/account-billing-information/utils/billingInformationViewed';
import { ACCOUNT_BILLING_INFORMATION_PATH } from '../config';

/**
 * Sends a billing admin to the billing information page once, when billing configuration is
 * required and not completed yet. After they have been to that page they are never redirected
 * again (they can skip it and come back from the navigation), the banner reminds them instead.
 */
export const BillingAdminGuard = () => {
  const { customerId, user, isBillingAdminForCustomer } = useAuthentication();
  const { pathname } = useLocation();

  const validCustomerId = customerId.value ?? '';
  const validUserId = user?.userId ?? '';
  const isBillingAdmin = !!validCustomerId && isBillingAdminForCustomer(validCustomerId);
  const isOnBillingPage = pathname.startsWith(ACCOUNT_BILLING_INFORMATION_PATH);
  const hasViewed = hasViewedBillingInformation(validCustomerId, validUserId);

  const { billingInformationStatus } = useGetBillingStatusQuery({
    customerId: validCustomerId,
    enabled: isBillingAdmin && !!validUserId && !hasViewed && !isOnBillingPage,
  });

  useEffect(() => {
    if (isBillingAdmin && isOnBillingPage) {
      markBillingInformationViewed(validCustomerId, validUserId);
    }
  }, [isBillingAdmin, isOnBillingPage, validCustomerId, validUserId]);

  const shouldRedirect =
    isBillingAdmin &&
    !!validUserId &&
    !isOnBillingPage &&
    !hasViewed &&
    !!billingInformationStatus?.billingConfigurationRequired &&
    !billingInformationStatus.billingConfigured;

  if (shouldRedirect) {
    return (
      <Navigate to={`${ACCOUNT_BILLING_INFORMATION_PATH}?customerId=${validCustomerId}`} replace />
    );
  }

  return null;
};
